/**
 * A listing's climb: every payment plotted against the running lifetime total.
 *
 * Drawn as inline SVG from the real payment history — no charting library, and
 * no invented points. A listing with a single payment renders as a flat line,
 * which is the honest picture of a listing that has been bid on once. The line
 * draws itself in with stroke-dashoffset; the dash length is the measured path
 * length, so the draw is even rather than lurching.
 */

import { useId, type ReactNode } from 'react';

import { type BidHistoryPoint } from '@bidtohire/shared';

import { formatMoneyCents } from '../lib/format';

interface Plotted {
  readonly x: number;
  readonly y: number;
}

function plot(
  history: readonly BidHistoryPoint[],
  width: number,
  height: number,
  padding: number,
): Plotted[] {
  const totals = history.map((point) => point.totalCents);
  const highest = Math.max(...totals);
  const lowest = Math.min(0, ...totals);
  const span = highest - lowest || 1;
  const usableY = height - padding * 2;
  const usableX = width - padding * 2;

  return history.map((point, index) => ({
    x:
      history.length === 1
        ? padding + usableX
        : padding + (index / (history.length - 1)) * usableX,
    y: height - padding - ((point.totalCents - lowest) / span) * usableY,
  }));
}

function toPath(points: readonly Plotted[]): string {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const only = points[0] as Plotted;
    // One payment is a flat line across the full width, not a dot in space.
    return `M0 ${only.y.toFixed(1)} L${only.x.toFixed(1)} ${only.y.toFixed(1)}`;
  }
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
    .join(' ');
}

/** The measured length of the polyline, used as the dash length. */
function pathLength(points: readonly Plotted[]): number {
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const a = points[index - 1] as Plotted;
    const b = points[index] as Plotted;
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return Math.max(40, Math.ceil(total));
}

/* -------------------------------------------------------------------------- */
/* The row-sized spark: a bare line, sized for a leaderboard cell.             */
/* -------------------------------------------------------------------------- */

export function RowSparkline({
  history,
  width = 88,
  height = 26,
}: {
  readonly history: readonly BidHistoryPoint[];
  readonly width?: number;
  readonly height?: number;
}): ReactNode {
  if (history.length === 0) return <span className="spark-flat">—</span>;

  const points = plot(history, width, height, 2);
  const length = pathLength(points);

  return (
    <svg
      className="spark"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      aria-hidden="true"
      style={{ ['--spark-len' as string]: length }}
    >
      <path
        className="line"
        d={toPath(points)}
        stroke="#533afd"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* The full chart on the listing page.                                        */
/* -------------------------------------------------------------------------- */

const WIDTH = 342;
const HEIGHT = 86;
const PADDING = 5;
const BASELINE = HEIGHT - 5.5;

export function BidSparkline({
  history,
}: {
  readonly history: readonly BidHistoryPoint[];
}): ReactNode {
  const gradientId = useId().replace(/:/g, '');

  if (history.length === 0) {
    return (
      <p className="small muted" style={{ marginTop: 12 }}>
        No bids have been placed on this listing yet.
      </p>
    );
  }

  const points = plot(history, WIDTH, HEIGHT - 6, PADDING);
  const line = toPath(points);
  const length = pathLength(points);
  const last = points[points.length - 1] as Plotted;
  const first = points[0] as Plotted;
  const firstPoint = history[0] as BidHistoryPoint;
  const lastPoint = history[history.length - 1] as BidHistoryPoint;

  const area = `${line} L${last.x.toFixed(1)} ${BASELINE} L${(history.length === 1 ? 0 : first.x).toFixed(1)} ${BASELINE} Z`;

  return (
    <svg
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      fill="none"
      role="img"
      aria-label={`Bid history: ${history.length} ${history.length === 1 ? 'payment' : 'payments'} rising to ${formatMoneyCents(lastPoint.totalCents)}`}
      style={{ ['--spark-len' as string]: length }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#533afd" stopOpacity=".16" />
          <stop offset="1" stopColor="#533afd" stopOpacity="0" />
        </linearGradient>
      </defs>

      <line x1="0" y1={BASELINE} x2={WIDTH} y2={BASELINE} stroke="#e3e8ee" strokeWidth="1" />
      <line x1="0" y1={BASELINE / 2} x2={WIDTH} y2={BASELINE / 2} stroke="#eef2f7" strokeWidth="1" />

      <path d={area} fill={`url(#${gradientId})`} />
      <path
        className="spark-draw"
        d={line}
        stroke="#533afd"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <circle cx={last.x} cy={last.y} r="3.5" fill="#533afd" />
      <circle cx={last.x} cy={last.y} r="7" fill="#533afd" opacity=".18" />

      <title>
        {`${formatMoneyCents(firstPoint.totalCents)} → ${formatMoneyCents(lastPoint.totalCents)}`}
      </title>
    </svg>
  );
}
